import { legalMessagesEn } from "@/i18n/locales/legalEnData";
import { pagesMessagesEn } from "@/i18n/locales/pagesEnData";

export const enMessages = {
  meta: {
    description:
      "WasOK — scan products, save ratings and notes, remember what was worth buying.",
    title: "WasOK — Remember what was worth buying",
  },
  header: {
    navFeatures: "Features",
    navHowItWorks: "How it Works",
    navPrivacy: "Privacy",
    navUseCases: "Use Cases",
    download: "Download",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    languageAria: "Language",
  },
  hero: {
    badge: "📱 Now Available on Android",
    title: "Remember what was worth buying.",
    subtitle:
      "Scan products, save ratings and notes, and instantly remember whether a product was actually good.",
    googlePlay: "Download on Google Play",
    watchDemo: "Watch Demo",
    privacyLine: "All data stays on your device.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Scan to remember",
    snackCard: "Delicious!",
    coffeeCard: "Too bitter",
    scrollHint: "Scroll to explore",
  },
  problems: {
    heading: "Sound familiar?",
    subheading:
      "We've all been there — staring at products, trying to remember if we liked them last time.",
    notedAgo: "✓ You noted this 3 months ago",
    items: [
      {
        question: "Which chocolate did I actually like?",
        note: "Perfect sweetness",
      },
      {
        question: "Why did I stop buying this shampoo?",
        note: "Dried out my hair",
      },
      {
        question: "Was this coffee good or disappointing?",
        note: "Too bitter",
      },
    ],
  },
  howItWorks: {
    heading: "How it works",
    subheading:
      "Four simple steps to never forget your product opinions again.",
    stepPrefix: "Step",
    steps: [
      {
        title: "Scan any product barcode",
        description:
          "Quick and easy scanning with your camera. Works with any barcode.",
      },
      {
        title: "Save ratings, comments & photos",
        description:
          "Rate products, add notes, attach photos, and log prices and locations.",
      },
      {
        title: "Instantly remember your experience",
        description:
          "Scan the same product later and see your previous rating and notes.",
      },
      {
        title: "Build smart shopping lists",
        description:
          "Create shopping lists from products you already trust and love.",
      },
    ],
  },
  privacy: {
    heading: "Your shopping history stays yours.",
    body: "WasOK helps you remember products you've tried while keeping your ratings, notes, photos, and shopping lists stored locally on your device.",
    trustBanner:
      "💚 Built around your shopping history—not around collecting your data.",
    bullets: [
      "No account required",
      "No cloud sync required",
      "Local-first storage",
      "User-controlled backups",
      "Optional barcode lookup for product information.",
      "Ad-supported with Google AdMob",
    ],
  },
  features: {
    heading: "Everything you need",
    subheading:
      "Powerful features designed to make product tracking simple and private.",
    items: [
      {
        title: "Barcode Scanner",
        description: "Fast and accurate barcode scanning for any product.",
      },
      {
        title: "Product Ratings",
        description: "Rate products from 1-5 stars to remember quality.",
      },
      {
        title: "Product Photos",
        description: "Attach photos to remember packaging or details.",
      },
      {
        title: "Categories",
        description: "Organize products into custom categories.",
      },
      {
        title: "Store Tracking",
        description: "Remember where you bought each product.",
      },
      {
        title: "Price Logging",
        description: "Track prices to find the best deals.",
      },
      {
        title: "Shopping Lists",
        description: "Build lists from your favorite products.",
      },
      {
        title: "Offline Access",
        description: "Works perfectly without internet connection.",
      },
      {
        title: "Local Storage",
        description: "All your data stored securely on your device.",
      },
    ],
  },
  useCases: {
    heading: "Perfect for every aisle",
    subheading:
      "Whether you're shopping for food, beauty, or household items — WasOK helps you remember.",
    cards: [
      {
        emoji: "🍫",
        title: "Snacks & Treats",
        description: "Remember which candies were actually good.",
      },
      {
        emoji: "☕",
        title: "Coffee & Beverages",
        description: "Track coffees worth buying again.",
      },
      {
        emoji: "💄",
        title: "Cosmetics & Skincare",
        description: "Keep notes about skin reactions.",
      },
      {
        emoji: "🧹",
        title: "Household Items",
        description: "Avoid rebuying products that failed.",
      },
    ],
  },
  screenshots: {
    heading: "Beautiful & intuitive",
    subheading:
      "A thoughtfully designed interface that makes tracking products a pleasure.",
    items: [
      { title: "Scanner" },
      { title: "Product Detail" },
      { title: "Rating Entry" },
      { title: "Search & History" },
      { title: "Shopping List" },
    ],
  },
  cta: {
    heading: "Shopping memory for real life.",
    body: "Stop forgetting which products were worth buying. Start shopping smarter today.",
    googlePlay: "Download on Google Play",
    scanQr: "Scan to download",
    tagline: "Private · Offline-first · Simple",
  },
  footer: {
    tagline: "Remember what was worth buying.",
    linksHeading: "Links",
    privacyPolicy: "Privacy Policy",
    terms: "Terms of Service",
    contact: "Contact",
    linkFaq: "FAQ",
    linkLicenses: "Open Source Licenses",
    linkAdvertising: "Advertising & Monetization",
    downloadHeading: "Download",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. All rights reserved. Made with 💚 for smarter shopping.",
  },
  legal: legalMessagesEn,
  faq: pagesMessagesEn.faq,
  support: pagesMessagesEn.support,
  advertising: pagesMessagesEn.advertising,
};

export type Messages = typeof enMessages;
